/**
 * Event Announcement Board — README "Operations tabs · UPDATES" (host) and the guest-facing board.
 *
 * Host (`isHost`): the composer (≤2000 chars with a visible counter, ≤4 images through
 * POST /events/{id}/announcements/image, no video), one white card per post with the
 * `2H AGO · SENT TO 12 GUESTS` line, inline EDIT / DELETE, the image strip and the
 * seen-count bar (`24 OF 28 SEEN` — seen counts are host-only). Guest: the same posts
 * with MARK SEEN — the only interaction the model allows. There are no replies or
 * reactions here by design; replies live in the group chat. Built on the host kit.
 *
 * `variant` is accepted for compatibility ('surface' is the only look now).
 */
import React, { useEffect, useRef, useState } from 'react';
import announcementService from '../services/announcementService';
import { useDialog } from '../context';
import { ago } from '../utils/surfaceFormat';
import { Card, Btn, Textarea, Track, Empty, ErrorLine } from './host/kit';

export const MAX_BODY = 2000;
export const MAX_IMAGES = 4;

const MUTED = '#6B7280';

/** `n of 2000 characters` — turns red at the cap. */
function Counter({ n, max }) {
  return <span style={{ fontSize: 11, color: n >= max ? '#DC2626' : MUTED, whiteSpace: 'nowrap' }}>{n} of {max} characters</span>;
}

/** The photo thumbnails (52px, radius 10, ✕) with the `+` button and the `n / 4 PHOTOS` count when adding. */
function ImageStrip({ urls = [], size = 52, onRemove, onAdd, remaining, uploading }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
      {onAdd && remaining > 0 && (
        <Btn variant="secondary" data-icon="" onClick={onAdd} disabled={uploading} aria-label="Add photo">{uploading ? '…' : '+'}</Btn>
      )}
      {(onAdd || urls.length > 0) && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: MUTED, whiteSpace: 'nowrap' }}>{urls.length} / {MAX_IMAGES} PHOTOS</span>}
      {urls.map((u, i) => (
        <span key={`${u}-${i}`} style={{ position: 'relative', flex: 'none', width: size, height: size, borderRadius: 10, overflow: 'visible' }}>
          <img src={u} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10, display: 'block' }} />
          {onRemove && (
            <button type="button" onClick={() => onRemove(i)} aria-label={`Remove photo ${i + 1}`} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, border: 'none', borderRadius: '50%', background: '#fff', color: '#DC2626', fontSize: 10, fontFamily: 'inherit', lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, boxShadow: '0 0 0 2px #fff' }}>✕</button>
          )}
        </span>
      ))}
    </div>
  );
}

function SeenBar({ seen, of, mobile }) {
  const pct = of > 0 ? Math.min(100, Math.round((seen / of) * 100)) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ flex: 1, minWidth: 0, maxWidth: mobile ? undefined : 240 }}><Track black pct={pct} /></span>
      <span style={{ flex: 'none', fontSize: 11, fontWeight: 700, color: MUTED, whiteSpace: 'nowrap' }} data-testid="seen-count">{of > 0 ? `${seen} OF ${of} SEEN` : `SEEN BY ${seen}`}</span>
    </div>
  );
}

export default function EventAnnouncementBoard({ eventId, isHost = false, audienceCount = null, mobile = false, onRowsChange, variant = 'surface' }) {
  const { showConfirm } = useDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // { id, body, imageUrls }
  const fileRef = useRef(null);

  const refresh = async () => {
    const r = await announcementService.list(eventId);
    if (r.success) {
      const list = r.data || [];
      setRows(list);
      onRowsChange?.(list);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (eventId) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_IMAGES - images.length;
    if (room <= 0) return;
    setUploading(true); setError(null);
    for (const file of files.slice(0, room)) {
      if (!file.type.startsWith('image/')) { setError('PHOTOS ONLY — NO VIDEO ON THE BOARD.'); continue; }
      // eslint-disable-next-line no-await-in-loop
      const r = await announcementService.uploadImage(eventId, file);
      if (r.success && r.data?.url) setImages((prev) => (prev.length < MAX_IMAGES ? [...prev, r.data.url] : prev));
      else setError((r.error || 'Upload failed').toString().toUpperCase());
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!body.trim() || submitting) return;
    setSubmitting(true); setError(null);
    const r = await announcementService.create(eventId, { body: body.trim(), imageUrls: images.length ? images : undefined });
    setSubmitting(false);
    if (r.success) { setBody(''); setImages([]); refresh(); }
    else setError((r.error || 'Could not post').toString().toUpperCase());
  };

  const saveEdit = async () => {
    if (!editing || !editing.body.trim()) return;
    const r = await announcementService.update(eventId, editing.id, { body: editing.body.trim(), imageUrls: editing.imageUrls });
    if (r.success) { setEditing(null); refresh(); }
    else setError((r.error || 'Could not save').toString().toUpperCase());
  };

  const remove = async (id) => {
    const ok = await showConfirm({ title: 'DELETE UPDATE', message: 'Delete this post? Guests who already saw it keep no copy.', confirmLabel: 'DELETE', variant: 'danger' });
    if (!ok) return;
    const r = await announcementService.delete(eventId, id);
    if (r.success) refresh();
  };

  const ack = async (id) => {
    const r = await announcementService.markSeen(eventId, id);
    if (r.success) refresh();
  };

  /** `2H AGO · SENT TO 12 GUESTS` for the host; the guest sees who posted it. */
  const whenLine = (p) => {
    const parts = [];
    if (!isHost && p.authorDisplayName) parts.push(String(p.authorDisplayName).toUpperCase());
    parts.push(ago(p.createdAt).toUpperCase());
    if (isHost && audienceCount != null) parts.push(`SENT TO ${audienceCount} GUESTS`);
    if (p.updatedAt) parts.push('EDITED');
    return parts.join(' · ');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="announcement-board" data-variant={variant}>
      {isHost && (
        <Card as="form" onSubmit={submit} gap="14" testId="announcement-composer">
          <span className="hev-sec-title">POST AN UPDATE</span>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
            maxLength={MAX_BODY}
            placeholder="Doors moved to 7, the alley gate is open…"
            rows={4}
            style={{ minHeight: 130 }}
            disabled={submitting}
            aria-label="Update"
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <ImageStrip urls={images} onRemove={(i) => setImages((p) => p.filter((_, j) => j !== i))} onAdd={pickFile} remaining={MAX_IMAGES - images.length} uploading={uploading} />
            <Counter n={body.length} max={MAX_BODY} />
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFile} data-testid="announcement-file" />
          <ErrorLine>{error}</ErrorLine>
          <Btn type="submit" size="lg" full disabled={submitting || uploading || !body.trim()} testId="post-update">{submitting ? 'POSTING…' : 'POST UPDATE'}</Btn>
          <span style={{ fontSize: 11, lineHeight: 1.6, color: MUTED }}>Everyone gets a notification. They can mark it seen — there are no replies here.</span>
        </Card>
      )}

      {loading ? (
        <span className="hev-caps-muted">LOADING…</span>
      ) : rows.length === 0 ? (
        <Card center>
          <Empty plain title="NO UPDATES YET.">{isHost ? 'Doors moved? Parking notes? Post it here and everyone who is in gets a notification.' : 'The host has not posted anything yet.'}</Empty>
        </Card>
      ) : (
        rows.map((p) => (
          <Card as="article" gap="8" key={p.id} testId={`announcement-${p.id}`}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>{whenLine(p)}</span>
            {editing?.id === p.id ? (
              <>
                <Textarea value={editing.body} onChange={(e) => setEditing((ed) => ({ ...ed, body: e.target.value.slice(0, MAX_BODY) }))} rows={4} aria-label="Edit update" />
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <ImageStrip urls={editing.imageUrls} size={44} onRemove={(idx) => setEditing((ed) => ({ ...ed, imageUrls: ed.imageUrls.filter((_, j) => j !== idx) }))} />
                  <Counter n={editing.body.length} max={MAX_BODY} />
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn size="sm" onClick={saveEdit} disabled={!editing.body.trim()} testId="save-edit">SAVE</Btn>
                  <Btn variant="secondary" size="sm" onClick={() => setEditing(null)}>CANCEL</Btn>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', textWrap: 'pretty', overflowWrap: 'anywhere' }}>{p.body}</p>
                {p.imageUrls?.length > 0 && <ImageStrip urls={p.imageUrls} size={mobile ? 72 : 96} />}
                {isHost ? (
                  <>
                    <SeenBar seen={p.seenCount || 0} of={audienceCount || 0} mobile={mobile} />
                    <div style={{ display: 'flex', gap: 6, paddingTop: 2 }}>
                      <Btn variant="secondary" size="xs" onClick={() => setEditing({ id: p.id, body: p.body, imageUrls: p.imageUrls || [] })} testId={`edit-${p.id}`}>EDIT</Btn>
                      <Btn variant="danger" size="xs" onClick={() => remove(p.id)} testId={`delete-${p.id}`}>DELETE</Btn>
                    </div>
                  </>
                ) : !p.viewerHasSeen ? (
                  <div><Btn variant="secondary" size="xs" onClick={() => ack(p.id)}>MARK SEEN</Btn></div>
                ) : (
                  <span className="hev-caps-muted" style={{ fontSize: 9 }}>SEEN</span>
                )}
              </>
            )}
          </Card>
        ))
      )}
    </div>
  );
}
